/**
 * Hooks TanStack Query — tarifs atelier (S6 PR5-bis).
 *
 * Pattern aligné sur `useDeliverableSettings`. La mutation update remplace
 * la query data au succès (`setQueryData`) plutôt que d'invalider — on a
 * déjà la response complète en retour, pas besoin d'un round-trip
 * supplémentaire.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { atelierSettingsApi } from '@/utils/api/atelier-settings'
import type {
  AtelierSettingsResponse,
  AtelierSettingsUpdateRequest,
  EtsyDefaultReadinessStateResponse,
  EtsyDefaultReadinessStateUpdateRequest,
  EtsyDefaultReturnPolicyResponse,
  EtsyDefaultReturnPolicyUpdateRequest,
  EtsyDefaultShippingProfileResponse,
  EtsyDefaultShippingProfileUpdateRequest,
  EtsyFreeShippingZonesResponse,
  EtsyFreeShippingZonesUpdateRequest,
  EtsyFreeShippingZonesUpdateResult,
  EtsyReadinessStateDefinitionSummary,
  EtsyReturnPolicySummary,
  EtsyShippingProfileSummary,
  PodDimensionsWhitelistResponse,
  PodDimensionsWhitelistUpdateRequest,
  PodFrameColorsProvisioningSummary,
  PodFrameColorsWhitelistResponse,
  PodFrameColorsWhitelistUpdateRequest,
  PodMatrixDefaultSpecByFinishResponse,
  PodMatrixDefaultSpecByFinishUpdateRequest,
} from '@/types/atelier-settings'

export const atelierSettingsKeys = {
  root: ['settings', 'atelier'] as const,
  etsyDefaultShippingProfile: ['settings', 'atelier', 'etsy-default-shipping-profile'] as const,
  etsyShippingProfiles: (connectionId: number) =>
    ['platform-connections', connectionId, 'etsy-shipping-profiles'] as const,
  etsyDefaultReadinessState: ['settings', 'atelier', 'etsy-default-readiness-state'] as const,
  etsyReadinessStateDefinitions: (connectionId: number) =>
    ['platform-connections', connectionId, 'etsy-readiness-state-definitions'] as const,
  etsyDefaultReturnPolicy: ['settings', 'atelier', 'etsy-default-return-policy'] as const,
  etsyReturnPolicies: (connectionId: number) =>
    ['platform-connections', connectionId, 'etsy-return-policies'] as const,
  etsyFreeShippingZones: ['settings', 'atelier', 'etsy-free-shipping-zones'] as const,
  podDimensionsWhitelist: ['settings', 'atelier', 'pod-dimensions-whitelist'] as const,
  podMatrixDefaultSpecByFinish: [
    'settings',
    'atelier',
    'pod-matrix-default-spec-by-finish',
  ] as const,
  podFrameColorsWhitelist: ['settings', 'atelier', 'pod-frame-colors-whitelist'] as const,
}

interface ApiError {
  detail: string
  status?: number
}

export function useAtelierSettings() {
  return useQuery<AtelierSettingsResponse>({
    queryKey: atelierSettingsKeys.root,
    queryFn: () => atelierSettingsApi.get(),
  })
}

export function useUpdateAtelierSettings() {
  const qc = useQueryClient()
  return useMutation<AtelierSettingsResponse, ApiError, AtelierSettingsUpdateRequest>({
    mutationFn: (payload) => atelierSettingsApi.update(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.root, data)
    },
  })
}

// PR-B FE — etsy_default_shipping_profile_id (KV séparé).

export function useEtsyDefaultShippingProfile() {
  return useQuery<EtsyDefaultShippingProfileResponse>({
    queryKey: atelierSettingsKeys.etsyDefaultShippingProfile,
    queryFn: () => atelierSettingsApi.getEtsyDefaultShippingProfile(),
  })
}

export function useUpdateEtsyDefaultShippingProfile() {
  const qc = useQueryClient()
  return useMutation<
    EtsyDefaultShippingProfileResponse,
    ApiError,
    EtsyDefaultShippingProfileUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updateEtsyDefaultShippingProfile(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.etsyDefaultShippingProfile, data)
    },
  })
}

/**
 * Liste live des shipping profiles Etsy d'une connexion. `enabled` à
 * false tant que `connectionId === null` (Etsy non connecté). Sur erreur
 * (503 OAuth expiré, 5xx Etsy down), le caller affiche un fallback input
 * number + un bouton "Reconnecter Etsy".
 */
export function useEtsyShippingProfiles(connectionId: number | null) {
  return useQuery<EtsyShippingProfileSummary[]>({
    queryKey: connectionId
      ? atelierSettingsKeys.etsyShippingProfiles(connectionId)
      : ['etsy-shipping-profiles', 'no-connection'],
    queryFn: () =>
      connectionId === null
        ? Promise.resolve([])
        : atelierSettingsApi.listEtsyShippingProfiles(connectionId),
    enabled: connectionId !== null,
    // Pas de retry sur erreur 503 (token Etsy expiré — l'utilisateur
    // doit re-OAuth, pas la peine de polluer le réseau).
    retry: false,
  })
}

// 2026-05-15 — readiness state Etsy par défaut (obligatoire physical/both).

export function useEtsyDefaultReadinessState() {
  return useQuery<EtsyDefaultReadinessStateResponse>({
    queryKey: atelierSettingsKeys.etsyDefaultReadinessState,
    queryFn: () => atelierSettingsApi.getEtsyDefaultReadinessState(),
  })
}

export function useUpdateEtsyDefaultReadinessState() {
  const qc = useQueryClient()
  return useMutation<
    EtsyDefaultReadinessStateResponse,
    ApiError,
    EtsyDefaultReadinessStateUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updateEtsyDefaultReadinessState(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.etsyDefaultReadinessState, data)
    },
  })
}

export function useEtsyReadinessStateDefinitions(connectionId: number | null) {
  return useQuery<EtsyReadinessStateDefinitionSummary[]>({
    queryKey: connectionId
      ? atelierSettingsKeys.etsyReadinessStateDefinitions(connectionId)
      : ['etsy-readiness-state-definitions', 'no-connection'],
    queryFn: () =>
      connectionId === null
        ? Promise.resolve([])
        : atelierSettingsApi.listEtsyReadinessStateDefinitions(connectionId),
    enabled: connectionId !== null,
    retry: false,
  })
}

// 2026-05-15 — return policy Etsy par défaut (obligatoire physical/both).

export function useEtsyDefaultReturnPolicy() {
  return useQuery<EtsyDefaultReturnPolicyResponse>({
    queryKey: atelierSettingsKeys.etsyDefaultReturnPolicy,
    queryFn: () => atelierSettingsApi.getEtsyDefaultReturnPolicy(),
  })
}

export function useUpdateEtsyDefaultReturnPolicy() {
  const qc = useQueryClient()
  return useMutation<
    EtsyDefaultReturnPolicyResponse,
    ApiError,
    EtsyDefaultReturnPolicyUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updateEtsyDefaultReturnPolicy(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.etsyDefaultReturnPolicy, data)
    },
  })
}

export function useEtsyReturnPolicies(connectionId: number | null) {
  return useQuery<EtsyReturnPolicySummary[]>({
    queryKey: connectionId
      ? atelierSettingsKeys.etsyReturnPolicies(connectionId)
      : ['etsy-return-policies', 'no-connection'],
    queryFn: () =>
      connectionId === null
        ? Promise.resolve([])
        : atelierSettingsApi.listEtsyReturnPolicies(connectionId),
    enabled: connectionId !== null,
    retry: false,
  })
}

// 2026-05-16 — pays free shipping baked (plan iridescent-sprouting-graham).

export function useEtsyFreeShippingZones() {
  return useQuery<EtsyFreeShippingZonesResponse>({
    queryKey: atelierSettingsKeys.etsyFreeShippingZones,
    queryFn: () => atelierSettingsApi.getEtsyFreeShippingZones(),
  })
}

export function useUpdateEtsyFreeShippingZonesWithBackfill() {
  const qc = useQueryClient()
  return useMutation<
    EtsyFreeShippingZonesUpdateResult,
    ApiError,
    EtsyFreeShippingZonesUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updateEtsyFreeShippingZonesWithBackfill(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.etsyFreeShippingZones, {
        value: data.value,
      })
    },
  })
}

// 2026-05-16 — whitelist POD dimensions (Catalogue POD tab).

export function usePodDimensionsWhitelist() {
  return useQuery<PodDimensionsWhitelistResponse>({
    queryKey: atelierSettingsKeys.podDimensionsWhitelist,
    queryFn: () => atelierSettingsApi.getPodDimensionsWhitelist(),
  })
}

export function useUpdatePodDimensionsWhitelist() {
  const qc = useQueryClient()
  return useMutation<PodDimensionsWhitelistResponse, ApiError, PodDimensionsWhitelistUpdateRequest>(
    {
      mutationFn: (payload) => atelierSettingsApi.updatePodDimensionsWhitelist(payload),
      onSuccess: (data) => {
        qc.setQueryData(atelierSettingsKeys.podDimensionsWhitelist, data)
      },
    },
  )
}

// Plan B 2026-05-18 — mapping atelier finish → printable_spec_id.

export function usePodMatrixDefaultSpecByFinish() {
  return useQuery<PodMatrixDefaultSpecByFinishResponse>({
    queryKey: atelierSettingsKeys.podMatrixDefaultSpecByFinish,
    queryFn: () => atelierSettingsApi.getPodMatrixDefaultSpecByFinish(),
  })
}

export function useUpdatePodMatrixDefaultSpecByFinish() {
  const qc = useQueryClient()
  return useMutation<
    PodMatrixDefaultSpecByFinishResponse,
    ApiError,
    PodMatrixDefaultSpecByFinishUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updatePodMatrixDefaultSpecByFinish(payload),
    onSuccess: (data) => {
      qc.setQueryData(atelierSettingsKeys.podMatrixDefaultSpecByFinish, data)
    },
  })
}

// Plan B Palier 3 2026-05-18 — whitelist FrameColor Gelato + provisioning.

export function usePodFrameColorsWhitelist() {
  return useQuery<PodFrameColorsWhitelistResponse>({
    queryKey: atelierSettingsKeys.podFrameColorsWhitelist,
    queryFn: () => atelierSettingsApi.getPodFrameColorsWhitelist(),
  })
}

export function useUpdatePodFrameColorsWhitelist() {
  const qc = useQueryClient()
  return useMutation<
    PodFrameColorsProvisioningSummary,
    ApiError,
    PodFrameColorsWhitelistUpdateRequest
  >({
    mutationFn: (payload) => atelierSettingsApi.updatePodFrameColorsWhitelist(payload),
    onSuccess: () => {
      // Le provisioning crée framings + specs + mapping — on invalide
      // largement pour que la UI re-fetch les rows concernées.
      void qc.invalidateQueries({ queryKey: atelierSettingsKeys.podFrameColorsWhitelist })
      void qc.invalidateQueries({ queryKey: atelierSettingsKeys.podMatrixDefaultSpecByFinish })
      void qc.invalidateQueries({ queryKey: ['catalog', 'framings'] })
      void qc.invalidateQueries({ queryKey: ['printable-specs'] })
    },
  })
}
